import type { IZynoPermissions } from '@zyno-platform/shared';
import type { RouteMeta } from 'vue-router';

import { computed } from 'vue';

import { GlobalState } from '@/global';
import { rmmAccess } from '@/shared/services/msp-org-access.service';

export function makePermissionMeta<A extends keyof IZynoPermissions, F extends keyof NonNullable<IZynoPermissions[A]>>(
    app: A,
    feature?: F | (() => F)
): RouteMeta {
    return { app, feature: feature as string | string | (() => string) | undefined };
}

// The ZynoConnect (ZC) master tenant. The cross-tenant SysAdmin console is only surfaced/reachable
// while the active tenant is ZC — a platform admin operating inside any other tenant does not see it.
export const ZC_TENANT_ID = '00000000-0000-0000-0000-000000000024';

// Gate for the SysAdmin console: an account-level platform admin whose active tenant is ZC.
export const canAccessSysAdmin = computed(() => !!GlobalState.user.value?.isSysAdmin && GlobalState.tenant.value?.id === ZC_TENANT_ID);

export function hasPermission<A extends keyof IZynoPermissions, F extends keyof NonNullable<IZynoPermissions[A]>>(
    app: A,
    feature?: F | (() => F)
): boolean;
export function hasPermission(app: string, feature?: string | (() => string)): boolean;
export function hasPermission(app: string, feature?: string | (() => string)): boolean {
    if (!GlobalState.permissions.value) return false;
    const appPermissions = GlobalState.permissions.value[app as keyof IZynoPermissions];
    if (!appPermissions) return false;
    if (!feature) return true;

    const featureKey = typeof feature === 'function' ? feature() : feature;
    return (appPermissions as Record<string, boolean | undefined>)[featureKey] === true;
}

function computeAuthorized(meta: RouteMeta): boolean | undefined {
    // special cases
    if (meta.app === 'msp') return rmmAccess.value.authorized;

    // The /msp partner console. `msp.login` is the MSP-partner capability (same one the tenant
    // switcher and MSP bridge key off), distinct from the `app: 'msp'` case above, which is RMM.
    // A route may carry `feature` to require a second msp capability on top of login — it is read
    // here rather than falling through to the `meta.app` branch below, which `msp` never reaches.
    if (meta.msp) return hasPermission('msp', 'login') && (meta.feature === undefined || hasPermission('msp', meta.feature));

    if (meta.sysadmin) return canAccessSysAdmin.value;
    // `admin` and `app` compose rather than short-circuit: a route carrying both requires tenant-admin
    // AND the permission. Previously `admin` returned early, so a route with both was gated on
    // tenant-admin alone and its permission was never consulted.
    if (meta.admin && !GlobalState.tenant.value?.isAdmin) return false;
    // NOTE: fail-open. A route with no `app` is visible to anyone who clears the checks above — most
    // routes (it, workstation, home) legitimately carry no permission meta, so this cannot simply be
    // flipped to deny; doing so needs a per-route audit plus an explicit "no permission required" marker.
    if (meta.app === undefined) return true;
    return hasPermission(meta.app, meta.feature);
}

export const routesWithAuthorization = computed(() => {
    const result = GlobalState.router
        .getRoutes()
        // .filter(r => r.children.length === 0) /* child routes contain the permissions */
        .map(r => ({
            path: r.path,
            pathRe: new RegExp(`^${r.path.replace(/:[^/]+/g, '.+')}$`),
            redirect: r.redirect,
            authorized: r.redirect ? null : computeAuthorized(r.meta)
        }));
    // Resolve a redirect option to its target path: strings directly; function redirects (e.g.
    // /sales → salespoint|bookings) evaluated with a stub `to` (param-dependent ones yield an unknown
    // path → unresolved); object redirects via `.path`.
    const redirectTargetPath = (redirect: unknown): string | undefined => {
        let target = redirect;
        if (typeof target === 'function') {
            try {
                target = (target as (to: unknown) => unknown)({ params: {}, query: {}, hash: '', path: '', fullPath: '', meta: {}, matched: [] });
            } catch {
                return undefined;
            }
        }
        return typeof target === 'string' ? target : (target as { path?: string } | null | undefined)?.path;
    };
    // A redirect route inherits its destination's authorization. Follow redirect chains (e.g.
    // /sales → /sales/bookings → /sales/bookings/calendar) to the real destination, order-independent.
    // biome-ignore lint/complexity/noForEach: <explanation>
    result
        .filter(r => r.redirect)
        .forEach(r => {
            const seen = new Set<string>();
            let dest = result.find(o => o.path === redirectTargetPath(r.redirect));
            while (dest?.redirect && !seen.has(dest.path)) {
                seen.add(dest.path);
                const nextPath = redirectTargetPath(dest.redirect);
                dest = result.find(o => o.path === nextPath);
            }
            r.authorized = dest?.authorized ?? false;
        });
    return result;
});

export function hasRouteAccess(path: string) {
    const matchingRoute = routesWithAuthorization.value.find(r => r.pathRe.test(path));
    return matchingRoute?.authorized === true;
}
